import type { LucideIcon } from 'lucide-react';

import { Logo } from '@/shared/components/brand/logo';

interface StatusPageProps {
  icon: LucideIcon;
  code: string;
  title: string;
  description: string;
  actions?: React.ReactNode;
}

/**
 * Layout shared by the full-page status screens (403, 404, route errors).
 *
 * Centralised so the three stay visually identical — they are the screens a
 * user sees when something has already gone wrong, which is the worst moment to
 * also look inconsistent.
 */
export function StatusPage({ icon: Icon, code, title, description, actions }: StatusPageProps) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-6 text-center">
      <Logo height={30} className="mb-10 opacity-80" />

      <div className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
        <Icon className="size-5" aria-hidden />
      </div>

      <p className="text-muted-foreground mt-6 font-mono text-xs font-medium tracking-widest">
        {code}
      </p>
      <h1 className="font-display mt-1.5 text-xl font-bold tracking-tight">{title}</h1>
      <p className="text-muted-foreground mt-2 max-w-md text-sm leading-relaxed">{description}</p>

      {actions && (
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">{actions}</div>
      )}
    </div>
  );
}
